import { ref, computed } from "vue";
import { useUserStore } from "@/store/userStore";
import { storeToRefs } from "pinia";
import Api from "@/model/api";
import { useRouter } from "vue-router";
import Tracking from "@/model/tracking.js";

// 全局状态
const isShowLoginDialog = ref(false);
const showSessionExpiredDialog = ref(false);
const qrcodeUrl = ref("");
const sceneId = ref("");
const checkLoginTimer = ref(null);
const isQrcodeExpired = ref(false);

// 登录场景枚举
const LoginSceneEnum = {
  NORMAL: Symbol("normal"),
  UPLOAD: Symbol("upload"),
};

// 登录场景类型
const loginSceneType = ref(LoginSceneEnum.NORMAL);

// 登录弹窗标题
const loginTitle = computed(() => {
  switch (loginSceneType.value) {
    case LoginSceneEnum.UPLOAD:
      return "扫码登录查看文件";
    default:
      return "微信扫码登录";
  }
});

// 登录弹窗副标题
const loginSubtitle = computed(() => {
  switch (loginSceneType.value) {
    case LoginSceneEnum.UPLOAD:
      return "永久云链已生成，请微信扫码登录后获取";
    default:
      return "微信扫码关注后完成登录，手机端可截图后扫码登录";
  }
});

export const useLogin = () => {
  const userStore = useUserStore();
  const { isLogin } = storeToRefs(userStore);
  const router = useRouter();

  // 获取登录二维码
  const getLoginQrcode = async () => {
    try {
      const result = await Api.getLoginQrcode();
      qrcodeUrl.value = result.qrcode_url;
      sceneId.value = result.scene_id;
      isQrcodeExpired.value = false;
      startCheckLoginStatus();
    } catch (error) {
      console.error("获取登录二维码失败:", error);
    }
  };

  // 开始检查登录状态
  const startCheckLoginStatus = () => {
    // 清除可能存在的旧定时器
    if (checkLoginTimer.value) {
      clearInterval(checkLoginTimer.value);
    }

    // 创建新的定时器，每3秒检查一次
    checkLoginTimer.value = setInterval(async () => {
      try {
        const result = await Api.checkLoginStatus(sceneId.value);

        switch (result.status) {
          case 0: // 未扫码
            break;
          case 1: // 新关注用户
          case 2: // 已关注用户
            // 保存 token 和 unionid
            userStore.setToken(result.jwt_token);
            userStore.setUnionid(result.unionid);
            // 发送登录成功埋点
            Tracking.send({
              trigger: "login-success",
              page: "main-page",
              dialog: "login-dialog",
              is_new_user_osa: result.status === 1,
              scene:
                loginSceneType.value === LoginSceneEnum.UPLOAD
                  ? "upload"
                  : "normal",
            });
            // 显示登录成功提示
            ElMessage({
              message: "登录成功",
              type: "success",
            });
            // 关闭弹窗
            closeLoginDialog();
            break;
          case 3: // 已过期
            isQrcodeExpired.value = true;
            clearInterval(checkLoginTimer.value);
            break;
        }
      } catch (error) {
        console.error("检查登录状态失败:", error);
        clearInterval(checkLoginTimer.value);
      }
    }, 3000);
  };

  // 刷新二维码
  const refreshQrcode = () => {
    getLoginQrcode();
  };

  // 打开登录弹窗
  const openLoginDialog = (scene = LoginSceneEnum.NORMAL) => {
    loginSceneType.value = scene;
    isShowLoginDialog.value = true;
    getLoginQrcode();
  };

  // 关闭登录弹窗
  const closeLoginDialog = () => {
    isShowLoginDialog.value = false;
    if (checkLoginTimer.value) {
      clearInterval(checkLoginTimer.value);
    }
    qrcodeUrl.value = "";
    sceneId.value = "";
    isQrcodeExpired.value = false;
  };

  // 登出
  const logout = async () => {
    try {
      await ElMessageBox.confirm("确定要退出登录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      });

      userStore.logout();
      // 发送登出成功埋点
      Tracking.send({
        trigger: "logout-success",
        page: "main-page",
      });
      ElMessage({
        message: "已退出登录",
        type: "success",
      });

      return true;
    } catch (error) {
      return false;
    }
  };

  // 处理会话过期
  const handleSessionExpired = (isHomePage) => {
    if (isHomePage) {
      closeLoginDialog();
      showSessionExpiredDialog.value = false;
    } else {
      isShowLoginDialog.value = false;
      showSessionExpiredDialog.value = true;
    }
  };

  const handleRelogin = () => {
    showSessionExpiredDialog.value = false;
    openLoginDialog();
  };

  const handleBackToHome = () => {
    showSessionExpiredDialog.value = false;
    router.push("/");
  };

  // 处理未授权(401)
  const handleUnauthorized = () => {
    userStore.logout();
    ElMessage.warning("登录已失效，请重新登录");
    openLoginDialog();
  };

  return {
    state: {
      isShowLoginDialog,
      isLogin,
      qrcodeUrl,
      isQrcodeExpired,
      showSessionExpiredDialog,
      loginTitle,
      loginSubtitle,
      LoginSceneEnum,
      loginSceneType,
    },
    action: {
      openLoginDialog,
      closeLoginDialog,
      refreshQrcode,
      logout,
      handleSessionExpired,
      handleRelogin,
      handleBackToHome,
      handleUnauthorized,
    },
  };
};
